import { useSyncExternalStore } from 'react';
import { WifiOff } from 'lucide-react';
import './OfflineBanner.css';
import { useTranslation } from 'react-i18next';

/**
 * Whether the browser thinks it has a connection.
 *
 * `useSyncExternalStore` rather than state and an effect: the value lives
 * outside React, and reading it at render time is what keeps the first frame
 * right after a reload with the cable already out.
 */
const subscribe = (fn: () => void) => {
  window.addEventListener('online', fn);
  window.addEventListener('offline', fn);
  return () => {
    window.removeEventListener('online', fn);
    window.removeEventListener('offline', fn);
  };
};

/**
 * „Fără conexiune" — spus o dată, sus, în loc de zece ori pe ecran.
 *
 * O clinică lucrează pe internetul ei, care mai cade. Fără linia asta, o
 * conexiune pierdută arată ca o aplicație stricată: butoanele răspund, dar
 * fiecare salvare pică cu un mesaj tehnic diferit, iar recepția reîncearcă
 * aceeași încasare de trei ori fiindcă nu are de unde ști că nu pleacă
 * nicăieri.
 *
 * `navigator.onLine` e o promisiune slabă — spune doar că există o interfață de
 * rețea, nu că serverul răspunde — deci textul spune „pare", nu „este". Fals
 * negativ nu dă: când browserul zice că e offline, chiar e.
 */
export function OfflineBanner() {
  const { t } = useTranslation('common');
  const online = useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    // On the server-rendered first pass there is no navigator; assume online,
    // because a banner that flashes on every load is worse than none.
    () => true,
  );

  if (online) return null;

  return (
    <div className="offline" role="status">
      <WifiOff size={15} aria-hidden />
      <span>
        <strong>{t('pareCaNuAi')}</strong> {t('ceScriiAcumNuSeSalveaza')}
      </span>
    </div>
  );
}
